import type { JSX } from "react";
import {
  formatCentsUsd,
  formatInterval,
  intervalSuffix,
  yearlySlotSavingsPercent,
  type SlotPrice,
} from "../../../types/billing";
import styles from "./SlotCadencePicker.module.css";

// Cadence choice for the pay-first slot purchase (IP-389): one option per
// buyable cadence with the real recurring price, so the customer sees what the
// slot bills BEFORE the Stripe checkout. A single-cadence price book renders as
// one (selected) option -- still worth showing for the price alone. Renders
// nothing when the backend offers no priced cadences; the caller's buy action
// then omits the interval and the checkout falls back to the plan's cadence.
export const SlotCadencePicker = ({
  prices,
  value,
  onChange,
  disabled = false,
}: {
  prices: readonly SlotPrice[];
  value: string | null;
  onChange: (interval: string) => void;
  disabled?: boolean;
}): JSX.Element | null => {
  if (prices.length === 0) return null;
  const savings = yearlySlotSavingsPercent(prices);
  return (
    <div className={styles.picker} role="group" aria-label="Slot billing cadence">
      {prices.map((p) => (
        <button
          key={p.interval}
          type="button"
          className={styles.option}
          data-selected={p.interval === value}
          aria-pressed={p.interval === value}
          disabled={disabled}
          onClick={() => onChange(p.interval)}
        >
          <span className={styles.optionLabel}>{formatInterval(p.interval)}</span>
          <span className={styles.optionPrice}>
            {formatCentsUsd(p.unitAmountCents)}
            {intervalSuffix(p.interval)}
          </span>
          {p.interval === "year" && savings !== null ? (
            <span className={styles.optionBadge}>save {savings}%</span>
          ) : null}
        </button>
      ))}
    </div>
  );
};
